import React, { useEffect, useState, useRef, Fragment, Component } from 'react';
import { Shield, Zap, Check, ArrowRight, AlertTriangle, Download, ChevronDown, ChevronUp, Clock, CreditCard, Star, X, MessageSquare, AlertCircle, Brain, DollarSign, Video, HardHat, CircuitBoard, Cpu, Settings, Target, TrendingUp, Lightbulb, Headphones, ThumbsUp, BookOpen, Lock, Gift } from 'lucide-react';
import { useMetaPixel } from '../hooks/useMetaPixel';
import { usePurchaseNotifications } from '../hooks/usePurchaseNotifications';
import { PurchaseNotification } from '../components/PurchaseNotification';
// FAQ Component
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border border-gray-200 rounded-lg mb-3 overflow-hidden bg-white" data-id="element-40" data-mp="2g0yqpm0aeafl">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors" data-id="element-41" data-mp="qlqutzmedw1f">
        <span className="font-medium text-gray-900 pr-4" data-id="element-42" data-mp="2zix9diyfpk0k">{question}</span>
        {isOpen ? <ChevronUp className="w-5 h-5 text-blue-600 flex-shrink-0" data-id="element-43" data-mp="2zgub6bn4okkr" /> : <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" data-id="element-44" data-mp="33lbq9xijm9p7" />}
      </button>
      {isOpen && <div className="px-6 py-4 bg-gray-50 border-t border-gray-200" data-id="element-45" data-mp="38itpgw6rdak0">
          <p className="text-gray-700 leading-relaxed" data-id="element-46" data-mp="3davbh9tqafn8">{answer}</p>
        </div>}
    </div>;
}
// Image Carousel Component
function ImageCarousel({
  images
}: {
  images: Array<{
    image: string;
    caption: string;
  }>;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      setCurrentIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
    }, 4000);
    return () => clearInterval(interval);
  }, [images.length, isPaused]);
  const handleTouchStart = (e: React.TouchEvent | React.MouseEvent) => {
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 5000);
    if ('touches' in e) {
      setTouchStart(e.targetTouches[0].clientX);
    } else {
      setTouchStart((e as React.MouseEvent).clientX);
    }
  };
  const handleTouchMove = (e: React.TouchEvent | React.MouseEvent) => {
    if ('touches' in e) {
      setTouchEnd(e.targetTouches[0].clientX);
    } else {
      setTouchEnd((e as React.MouseEvent).clientX);
    }
  };
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > 50) setCurrentIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
    if (distance < -50) setCurrentIndex(prev => prev === 0 ? images.length - 1 : prev - 1);
    setTouchEnd(0);
    setTouchStart(0);
  };
  return <div className="relative max-w-2xl mx-auto touch-pan-y" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} onMouseDown={handleTouchStart} onMouseMove={handleTouchMove} onMouseUp={handleTouchEnd} onMouseLeave={handleTouchEnd} data-id="element-47" data-mp="aqbmhovp1qjr">
      <div className="relative bg-gray-50 border-2 border-gray-200 rounded-xl overflow-hidden shadow-lg select-none cursor-grab active:cursor-grabbing" data-id="element-48" data-mp="uais79ckw4ug">
        <div className="aspect-[3/4] relative" data-id="element-49" data-mp="kdnswo8he0f4">
          <img src={images[currentIndex].image} alt={images[currentIndex].caption} className="w-full h-full object-cover transition-opacity duration-500 pointer-events-none" data-id="element-50" data-mp="kccqarqruypl" />
        </div>
        <div className="p-4 bg-white border-t border-gray-100" data-id="element-51" data-mp="be12exhaif0u">
          <p className="text-sm font-medium text-gray-700 text-center" data-id="element-52" data-mp="3stpp12qbkvb2">
            {images[currentIndex].caption}
          </p>
        </div>
      </div>
      <div className="flex justify-center gap-2 mt-6" data-id="element-53" data-mp="1no84djob7hi">
        {images.map((_, index) => <button key={index} onClick={() => {
        setCurrentIndex(index);
        setIsPaused(true);
        setTimeout(() => setIsPaused(false), 5000);
      }} className={`h-2 rounded-full transition-all duration-300 ${index === currentIndex ? 'bg-blue-600 w-8' : 'bg-gray-300 w-2'}`} aria-label={`Ir a imagen ${index + 1}`} data-id="element-54" data-mp="3jtubwsd7eq8e" data-mpi={index} />)}
      </div>
    </div>;
}
// Dynamic Stats Hook
function useDynamicStats() {
  const [viewers, setViewers] = useState(47);
  const [spots, setSpots] = useState(9);
  useEffect(() => {
    const viewerInterval = setInterval(() => {
      setViewers(prev => {
        const change = Math.floor(Math.random() * 5) - 2;
        return Math.max(40, Math.min(55, prev + change));
      });
    }, Math.random() * 5000 + 8000);
    const spotInterval = setInterval(() => {
      setSpots(prev => prev > 3 && Math.random() > 0.65 ? prev - 1 : prev);
    }, 40000);
    return () => {
      clearInterval(viewerInterval);
      clearInterval(spotInterval);
    };
  }, []);
  return {
    viewers,
    spots
  };
}
// Upsell Stats Hook
function useUpsellStats() {
  const [viewers, setViewers] = useState(62);
  const [spots, setSpots] = useState(12);
  const [professionals, setProfessionals] = useState(5851);
  useEffect(() => {
    const viewerInterval = setInterval(() => {
      setViewers(prev => {
        const change = Math.floor(Math.random() * 5) - 2;
        return Math.max(55, Math.min(75, prev + change));
      });
    }, 5000);
    const spotInterval = setInterval(() => {
      setSpots(prev => {
        if (prev > 2) {
          setProfessionals(p => p + 1);
          return prev - 1;
        }
        return prev;
      });
    }, 40000);
    return () => {
      clearInterval(viewerInterval);
      clearInterval(spotInterval);
    };
  }, []);
  return {
    viewers,
    spots,
    professionals
  };
}
// Upsell Section Component
function UpsellSection() {
  const {
    viewers,
    spots,
    professionals
  } = useUpsellStats();
  const today = new Date();
  const formattedDate = today.toLocaleDateString('es-AR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  });
  const displayDate = formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
  return;
}
// Review Form Component
function ReviewForm({
  onSubmitReview
}: {
  onSubmitReview: (review: {
    name: string;
    text: string;
    stars: number;
  }) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [comment, setComment] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');
  const handleSubmit = () => {
    if (!name.trim()) {
      setError('Por favor ingresá tu nombre');
      return;
    }
    if (rating === 0) {
      setError('Por favor seleccioná una calificación');
      return;
    }
    if (!comment.trim()) {
      setError('Por favor escribí un comentario');
      return;
    }
    setError('');
    setShowSuccess(true);
    onSubmitReview({
      name: name.trim(),
      text: comment.trim(),
      stars: rating
    });
    setName('');
    setComment('');
    setRating(0);
    setTimeout(() => {
      setShowSuccess(false);
      setIsOpen(false);
    }, 3000);
  };
  if (!isOpen) {
    return <div className="text-center mt-8" data-id="element-55" data-mp="21ulrlslmo04x">
        <button onClick={() => setIsOpen(true)} className="text-gray-500 hover:text-blue-600 text-sm font-medium underline transition-colors" data-id="element-56" data-mp="21vofdrnnlmgm">
          ¿Compraste? Dejá tu reseña
        </button>
      </div>;
  }
  return <div className="bg-white border border-gray-200 rounded-xl p-6 mt-8 max-w-2xl mx-auto shadow-sm relative animate-fade-in-up" data-id="element-57" data-mp="2ugocz4pn2jnd">
      {showSuccess && <div className="absolute top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg text-sm font-medium z-10" data-id="element-58" data-mp="2liuk63r3284y">
          <p data-id="element-59" data-mp="2pi3eb9q1c811">¡Gracias por tu reseña!</p>
        </div>}
      <div className="flex justify-between items-center mb-4" data-id="element-60" data-mp="1a35v96u9qz1i">
        <h4 className="font-bold text-gray-900 flex items-center gap-2" data-id="element-61" data-mp="1a50i4qp3p188">
          <MessageSquare className="w-5 h-5 text-blue-600" data-id="element-62" data-mp="1e5anc152jnm1" />
          Dejá tu reseña
        </h4>
        <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-gray-600" data-id="element-63" data-mp="25svj7b0k0gcm">
          <X className="w-5 h-5" data-id="element-64" data-mp="20s7wh1jcylo7" />
        </button>
      </div>
      <div className="space-y-4" data-id="element-65" data-mp="1wr4di660k9ir">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-66" data-mp="1ws74sf790dz7">
          <input type="text" placeholder="Tu nombre" value={name} onChange={e => setName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" data-id="element-67" data-mp="1rskuoempvdum" />
          <div className="flex items-center gap-1 px-4 py-2 border border-gray-300 rounded-lg bg-white" data-id="element-68" data-mp="2khs07xkl93pd">
            {[1, 2, 3, 4, 5].map(s => <Star key={s} className={`w-5 h-5 cursor-pointer transition-colors ${s <= (hoverRating || rating) ? 'fill-orange-400 text-orange-400' : 'text-gray-300'}`} onMouseEnter={() => setHoverRating(s)} onMouseLeave={() => setHoverRating(0)} onClick={() => setRating(s)} data-id="element-69" data-mp="2ar6tfmurgsvd" />)}
          </div>
        </div>
        <textarea placeholder="Contanos tu experiencia..." rows={3} value={comment} onChange={e => setComment(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all resize-none" data-id="element-70" data-mp="3ar8z363vfuc5" />
        {error && <p className="text-red-500 text-sm" data-id="element-71" data-mp="3oqiei3k96kf2">{error}</p>}
        <button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg transition-colors text-sm" data-id="element-72" data-mp="3osbitc7e8sjw">
          Enviar reseña
        </button>
      </div>
    </div>;
}
// Testimonial Item with Like functionality
function TestimonialItem({
  t
}: {
  t: any;
}) {
  const [likes, setLikes] = useState(Math.floor(Math.random() * 100) + 20);
  const [liked, setLiked] = useState(false);
  const handleLike = () => {
    if (liked) {
      setLikes(prev => prev - 1);
      setLiked(false);
    } else {
      setLikes(prev => prev + 1);
      setLiked(true);
    }
  };
  return <div className="flex items-start gap-3" data-id="element-73" data-mp="l7yrpgkcg6rn">
      <div className="w-9 h-9 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0" data-id="element-74" data-mp="1g7zuzhqlcw1o">
        {t.name.charAt(0)}
      </div>
      <div className="flex-grow" data-id="element-75" data-mp="1q5e7j69e90op">
        <div className="flex items-center gap-2 mb-1" data-id="element-76" data-mp="1q4cx9407ci81">
          <span className="font-bold text-sm text-gray-800" data-id="element-77" data-mp="1uw4e2mtk39kz">{t.name}</span>
          {t.verified && <span className="text-[9px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium" data-id="element-78" data-mp="1uxxomd7aidy9">
              ✓ Verificado
            </span>}
        </div>
        <div className="chat-bubble" data-id="element-79" data-mp="23v7ybu4glv12">
          <p className="text-sm text-gray-800 leading-relaxed" data-id="element-80" data-mp="28vw0nby51yal">{t.msg}</p>
          <div className="flex justify-between items-center mt-2" data-id="element-81" data-mp="28wni7iuwhvhv">
            <p className="text-[10px] text-gray-400" data-id="element-82" data-mp="2dtxjpwvkdxm2">{t.time}</p>
            <button onClick={handleLike} className={`flex items-center gap-1 text-[10px] font-medium transition-colors ${liked ? 'text-blue-600' : 'text-gray-500 hover:text-blue-600'}`} data-id="element-83" data-mp="ei4h9kq0c01y">
              <ThumbsUp className={`w-3 h-3 ${liked ? 'fill-blue-600' : ''}`} data-id="element-84" data-mp="2wr7f8osekg49" />
              {likes}
            </button>
          </div>
        </div>
      </div>
    </div>;
}
export function LandingPage() {
  const {
    trackEvent
  } = useMetaPixel();
  const {
    notification,
    isVisible
  } = usePurchaseNotifications();
  const {
    viewers,
    spots
  } = useDynamicStats();
  const [argentinaNow, setArgentinaNow] = useState(() => new Date());
  const formattedDate = argentinaNow.toLocaleDateString('es-AR', {
    timeZone: 'America/Argentina/Buenos_Aires',
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  });
  const displayDate = formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
  const [headerVisible, setHeaderVisible] = useState(false);
  const [problemRealVisible, setProblemRealVisible] = useState(false);
  const [decisionVisible, setDecisionVisible] = useState(false);
  const [benefitsVisible, setBenefitsVisible] = useState(false);
  const [postHeroVisible, setPostHeroVisible] = useState(false);
  const [visibleTestimonials, setVisibleTestimonials] = useState(5);
  const [activeBonus, setActiveBonus] = useState(0);
  const [finalComboVisible, setFinalComboVisible] = useState(false);
  const [includedContentVisible, setIncludedContentVisible] = useState(false);
  const bonusScrollRef = useRef<HTMLDivElement>(null);
  const finalComboRef = useRef<HTMLElement>(null);
  const includedContentRef = useRef<HTMLDivElement>(null);
  // Reviews pool
  const allChatReviews = [{
    name: 'Javier M.',
    msg: 'Che, posta que me cambió la cabeza. Antes veía el PLC y me daba respeto tocar, ahora entro al código y sé exactamente qué buscar 💪',
    time: '14:32',
    verified: true
  }, {
    name: 'Gustavo R.',
    msg: 'Re sirve loco. Directo al grano para los que estamos en la planta. Los diagramas de lógica están claritos.',
    time: '09:15',
    verified: true
  }, {
    name: 'Pablo S.',
    msg: 'Me salvó las papas con una línea parada. No le encontraban la vuelta y con el método de diagnóstico lo saqué de taquito 🔧',
    time: '18:47',
    verified: true
  }, {
    name: 'Andrés C.',
    msg: 'Vale cada peso. Solo con la guía de fallas comunes ya se paga solo el material. Lo uso siempre.',
    time: '11:03',
    verified: true
  }, {
    name: 'Matías B.',
    msg: 'Laburo en mantenimiento y esto me ordenó la lógica mal. Antes adivinaba, ahora entiendo qué hace la máquina ⚡',
    time: '16:22',
    verified: true
  }];
  // Scroll reveal for sections
  useEffect(() => {
    const revealObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-reveal');
          revealObserver.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1
    });
    document.querySelectorAll('[data-reveal]').forEach(el => {
      revealObserver.observe(el);
    });
    return () => revealObserver.disconnect();
  }, []);
  // Header visibility on scroll
  useEffect(() => {
    const handleScroll = () => {
      setHeaderVisible(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          if (entry.target.id === 'problem-real-section') setProblemRealVisible(true);
          if (entry.target.id === 'decision-section') setDecisionVisible(true);
          if (entry.target.id === 'benefits-section') setBenefitsVisible(true);
          if (entry.target.id === 'post-hero-section') setPostHeroVisible(true);
        }
      });
    }, {
      threshold: 0.15
    });
    const sections = ['problem-real-section', 'decision-section', 'benefits-section', 'post-hero-section'];
    sections.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);
  const scrollToPurchase = () => {
    document.getElementById('compra')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const scrollToSolution = () => {
    document.getElementById('solution')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const buildCheckoutUrl = () => {
    const checkoutUrl = new URL('https://plc-master.impultienda.ar/checkout');
    const campaignParams = new URLSearchParams(window.location.search);
    campaignParams.forEach((value, key) => checkoutUrl.searchParams.set(key, value));
    return checkoutUrl.toString();
  };
  const handlePurchaseClick = () => {
    trackEvent('InitiateCheckout');
    window.location.href = buildCheckoutUrl();
  };
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          trackEvent('ViewContent', {
            content_name: 'Bonuses Section'
          });
          observer.disconnect();
        }
      });
    }, {
      threshold: 0.5
    });
    const bonusSection = document.getElementById('bonuses');
    if (bonusSection) observer.observe(bonusSection);
    return () => observer.disconnect();
  }, [trackEvent]);
  useEffect(() => {
    const section = finalComboRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting) {
        setFinalComboVisible(true);
        observer.disconnect();
      }
    }, {
      threshold: 0.2
    });
    observer.observe(section);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const summary = includedContentRef.current;
    if (!summary) return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting) {
        setIncludedContentVisible(true);
        observer.disconnect();
      }
    }, {
      threshold: 0.35
    });
    observer.observe(summary);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const interval = window.setInterval(() => setArgentinaNow(new Date()), 60_000);
    return () => window.clearInterval(interval);
  }, []);
  const contentImages = [{
    image: 'https://cdn.magicpatterns.com/uploads/6N9aigZzaNsoRe37pWdoFH/WhatsApp_Image_2026-01-17_at_13.42.51.jpg',
    caption: 'Diagramas explicados paso a paso'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/aRdF2bWiMHEmctuHXsJVR9/WhatsApp_Image_2026-01-17_at_13.42.50_(3).jpg',
    caption: 'Entendé la lógica del circuito, no solo la conexión'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/fuLknpKftbKZSC5DGAQ2tZ/WhatsApp_Image_2026-01-17_at_13.42.50_(2).jpg',
    caption: 'Casos prácticos aplicados al trabajo real'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/aGWc1fPM9DTj6kfR5oKjFr/WhatsApp_Image_2026-01-17_at_13.42.50_(1).jpg',
    caption: 'Material pensado para uso profesional'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/dZNF3VrgumsrSXiMRXLWTh/WhatsApp_Image_2026-01-17_at_13.42.49.jpg',
    caption: 'Explicaciones técnicas claras y directas'
  }];
  const showMoreTestimonials = () => {
    setVisibleTestimonials(prev => Math.min(prev + 5, allChatReviews.length));
  };
  const handleSubmitReview = (review: {
    name: string;
    text: string;
    stars: number;
  }) => {
    // Handle review submission
  };
  const handleBonusScroll = (index: number) => {
    setActiveBonus(index);
    if (bonusScrollRef.current) {
      const cardWidth = 320; // Approx width of card + gap
      bonusScrollRef.current.scrollTo({
        left: index * cardWidth,
        behavior: 'smooth'
      });
    }
  };
  const problemItems = [{
    icon: AlertTriangle,
    title: 'EL MIEDO A LO "DIFÍCIL"',
    desc: 'Sentir que la automatización es solo para ingenieros o genios de la matemática, cuando en realidad es pura lógica de campo.'
  }, {
    icon: Lock,
    title: 'EL MURO LABORAL',
    desc: 'Ver que todos los puestos bien pagados en la industria exigen PLC y sentir que te quedás afuera por no tener la base técnica.'
  }, {
    icon: Zap,
    title: 'EL TECHO DEL ELECTRICISTA',
    desc: 'Estar cansado del esfuerzo físico extremo y los trabajos mal pagados de siempre, buscando pasar a la parte inteligente del rubro.'
  }, {
    icon: Brain,
    title: 'EL LABERINTO DE YOUTUBE',
    desc: 'Perder meses con tutoriales sueltos que no tienen un orden lógico y te terminan dejando más confundido que al principio.'
  }];
  const ecosystemItems = [{
    icon: Video,
    title: '+100 CLASES',
    desc: 'Desde cero hasta programación avanzada.'
  }, {
    icon: Cpu,
    title: 'SOFTWARE Y SIMULADORES',
    desc: 'TIA Portal, Studio 5000 y RSLogix 500.'
  }, {
    icon: Download,
    title: 'MANUALES PDF',
    desc: 'Diagramas, guías y documentación técnica.'
  }, {
    icon: HardHat,
    title: 'VIRTUALBOX',
    desc: 'Entorno virtual preconfigurado para practicar.'
  }, {
    icon: CircuitBoard,
    title: 'CURSOS COMPLEMENTARIOS',
    desc: 'HMI, SCADA y redes industriales: Profinet / Modbus.'
  }];
  const bonuses = [{
    title: 'CURSO COMPLETO DE SCADA',
    desc: 'Visualización y control de procesos en tiempo real.',
    img: 'https://cdn.magicpatterns.com/uploads/vy87Pj4Sj47qBszJjQkhfj/Gemini_Generated_Image_vp50nqvp50nqvp50.png',
    value: '$14.990 ARS',
    label: 'HOY: GRATIS'
  }, {
    title: 'MÓDULO EXTRA DE PLC',
    desc: 'Especialización en Logo Siemens y RSLogix.',
    img: 'https://cdn.magicpatterns.com/uploads/eSbaAdqFeccZmE2NYRzeGL/Gemini_Generated_Image_ofo2vrofo2vrofo2.png',
    value: '$9.990 ARS',
    label: 'HOY: GRATIS'
  }, {
    title: 'CONTROL DE PROCESOS',
    desc: 'Dominio de sistemas continuos y discretos.',
    img: 'https://cdn.magicpatterns.com/uploads/aP18Z5MXiG8xCyEV7bBRs5/Gemini_Generated_Image_o5jpmxo5jpmxo5jp.png',
    value: '$6.990 ARS',
    label: 'HOY: GRATIS'
  }, {
    title: 'VARIADORES Y SENSORES',
    desc: 'Integración avanzada de hardware industrial.',
    img: 'https://cdn.magicpatterns.com/uploads/kanbA5DbyaFJMFSd93R4q2/Gemini_Generated_Image_2bzo742bzo742bzo.png',
    value: '$8.990 ARS',
    label: 'HOY: GRATIS'
  }];
  return <div className="min-h-screen bg-gray-50 font-sans text-gray-800" data-id="element-85" data-mp="3f7p3bozoommy">
      {/* Header - Hidden initially, appears on scroll */}
      <header className={`bg-blue-900 text-white fixed top-0 left-0 right-0 z-40 shadow-md transition-all duration-500 ${headerVisible ? 'animate-header-slide' : 'opacity-0 -translate-y-full pointer-events-none'} ${isVisible ? 'py-5' : 'py-3'}`} data-id="element-86" data-mp="3jb3lal76nd31">
        <div className="container mx-auto px-4 flex justify-between items-center transition-all duration-500" data-id="element-87" data-mp="1tal16agcjfc">
          <div className="flex items-center gap-3 overflow-hidden flex-1" data-id="element-88" data-mp="6sn5jkdupeer">
            <div className={`rounded-full overflow-hidden border-2 border-white/20 animate-logo-pulse flex-shrink-0 transition-all duration-500 ${isVisible ? 'w-12 h-12' : 'w-10 h-10'}`} data-id="element-89" data-mp="atqmybbjrii3">
              <img src="https://cdn.magicpatterns.com/uploads/v3vJ7LASUzqrw9kw6Gkeea/Diseno_sin_titulo_(5).png" alt="PLC MASTER Logo" className="w-full h-full object-cover" data-id="element-90" data-mp="fqh1uvbm881x" />
            </div>
            {notification && <PurchaseNotification name={notification.name} city={notification.city} product={notification.product} isVisible={isVisible} isHeader={true} data-id="element-91" data-mp="2llrhraprqnme" />}
          </div>
          <button onClick={scrollToPurchase} className={`${`bg-orange-500 hover:bg-orange-400 text-white font-bold rounded transition-all duration-500 flex-shrink-0 ml-2 ${isVisible ? 'py-3 px-6 text-base' : 'py-2 px-4 text-sm'}`} text-[12px] pt-[5px] pb-[5px] pl-[15px] pr-[15px]`} data-id="element-92" data-mp="2gtq8zyrt7f14">
            VER OFERTA
          </button>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden px-4 pb-5 pt-3 text-white md:pb-10 md:pt-20" style={{
      background: 'radial-gradient(circle at 50% 32%, #1d4e98 0%, #102d62 31%, #06152f 72%, #041025 100%)'
    }} data-id="element-93" data-mp="2zmlug5junaqv">
        <div className="relative z-10 mx-auto max-w-5xl text-center" data-id="element-94" data-mp="1592ruhayp1yp">
          <p className="mx-auto mb-2 inline-flex h-6 items-center rounded-full border border-orange-400/45 bg-orange-500/10 px-3 text-[9px] font-extrabold tracking-[0.06em] text-orange-100 sm:text-xs" data-id="element-95" data-mp="3gvwfts7fp1eh">
            ⚡ ATENCIÓN: ELECTRICISTAS Y TÉCNICOS
          </p>

          <h1 className="mx-auto max-w-4xl text-[34px] font-black leading-[0.94] tracking-[-0.055em] sm:text-5xl md:text-7xl" data-id="element-96" data-mp="3c44vw0is9txb">
            <span className="block" style={{
            paddingTop: "21px"
          }} data-id="element-97" data-mp="2y43vsnaceqiw">¿QUERÉS COBRAR MEJOR?</span>
            <span className="mt-1 block text-orange-500" data-id="element-98" data-mp="3349h7lq0rgsj">APRENDÉ PLC.</span>
          </h1>

          <p className="mx-auto mt-2 max-w-md text-center text-[13px] font-medium leading-snug text-blue-100/85 sm:mt-3 sm:text-base md:text-xl" style={{
          paddingTop: "13px"
        }} data-id="element-99" data-mp="2p4qmpvq4s3yp">
            Aprovechá tu experiencia como electricista para dar el salto a la automatización industrial. Aprendé <strong className="font-extrabold text-white" data-id="element-100" data-mp="rcw5ga7dcl96">Ladder, LOGO! V8 y S7-1200</strong> y preparate para ofrecer trabajos de <strong className="font-extrabold text-orange-300" data-id="element-101" data-mp="2mrivfq734jz">mayor valor técnico y económico</strong>.
          </p>

          <div className="hero-product-stage relative mx-auto mt-2 flex w-full max-w-[560px] justify-center sm:mt-3" data-id="element-102" data-mp="3trhsvw83n4i7">
            <div className="hero-product-shadow pointer-events-none absolute inset-x-[18%] bottom-[8%] z-0 h-5" aria-hidden="true" data-id="element-103" data-mp="3jvetuz8jnlzh"></div>
            <div className="hero-product-float relative z-10 w-full" data-id="element-104" data-mp="3fuuvxtg5ceca">
              <img src="https://cdn.magicpatterns.com/uploads/ffLLqHcfTafcxhdzomeyG1/Diseno_sin_titulo_(5).webp" alt="Manuales PLC MASTER y Variadores de Frecuencia" className="hero-product-enter block h-auto w-full cursor-pointer object-contain" onClick={scrollToPurchase} data-id="element-105" data-mp="3b3cvd6i1cfru" />
            </div>
          </div>

          <div className="mx-auto mt-0 flex w-full max-w-md items-center justify-between px-2 text-white sm:px-0" data-id="element-106" data-mp="2w6c1oxgaaaqq">
            <div className="flex min-w-0 flex-1 flex-col items-center gap-1 text-center" data-id="element-107" data-mp="2s59zq8uzfkl2">
              <Video className="h-4 w-4 text-orange-400" data-id="element-108" data-mp="17my48mse2wpx" />
              <span className="text-[9px] font-black leading-tight sm:text-[11px]" data-id="element-109" data-mp="17oixm92yw9m7">+100 CLASES</span>
            </div>
            <div className="flex min-w-0 flex-1 flex-col items-center gap-1 text-center" data-id="element-110" data-mp="1gn4cnu98d6i8">
              <Cpu className="h-4 w-4 text-orange-400" data-id="element-111" data-mp="1llxqxowcd1jg" />
              <span className="text-[9px] font-black leading-tight sm:text-[11px]" data-id="element-112" data-mp="1llfmi4zizh0g">SIMULADORES</span>
            </div>
            <div className="flex min-w-0 flex-1 flex-col items-center gap-1 text-center" data-id="element-113" data-mp="nw473ev93uog">
              <Download className="h-4 w-4 text-orange-400" data-id="element-114" data-mp="sxjtpbd2u98y" />
              <span className="text-[9px] font-black leading-tight sm:text-[11px]" data-id="element-115" data-mp="xw3eh8fxpezq">MANUALES PDF</span>
            </div>
          </div>

          <div className="relative mx-auto mt-3 w-[93%] max-w-[370px]" data-id="element-116" data-mp="12oegcgu8jaux">
            <div className="pointer-events-none absolute inset-x-[10%] top-1/2 h-6 -translate-y-1/2 rounded-full bg-blue-400/25 blur-[16px]" data-id="element-117" data-mp="56o7ctre4693"></div>
            <div className="pointer-events-none absolute inset-x-[18%] bottom-0 h-3 rounded-full bg-black/35 blur-md" data-id="element-118" data-mp="a4fvrdstta8j"></div>
            <div className="plc-social-shine relative z-10 overflow-hidden" data-id="element-119" data-mp="3ua5g9a6z6ap9">
              <img src="https://cdn.magicpatterns.com/uploads/56uRXiKVB3v3UQVQBZQpik/Valoraciones_de_Maestro_Electrico.webp" alt="Valoración de profesionales y creadores de Maestro Eléctrico" className="plc-social-float relative z-10 block w-full -my-[8%] object-contain" data-id="element-120" data-mp="3u82mqg6m5zfs" />
            </div>
          </div>

          <button onClick={scrollToPurchase} className="btn-shine mt-2 inline-flex w-full max-w-md items-center justify-center rounded-xl bg-orange-500 px-6 py-3.5 text-base font-black text-white shadow-lg shadow-orange-950/40 transition-colors duration-200 hover:bg-orange-400 sm:mt-2 sm:w-auto sm:px-8 sm:py-4 sm:text-xl" data-id="element-121" data-mp="3bb1803n9ckyn">
            QUIERO ACCEDER A PLC MASTER →
          </button>

          <div className="mx-auto mt-2 flex max-w-md items-center justify-center gap-1.5 whitespace-nowrap text-[8px] font-bold tracking-wide text-blue-100/75 sm:gap-3 sm:text-[11px]" data-id="element-122" data-mp="14megs7t76hiz">
            <span data-id="element-123" data-mp="2hl7yoqrwpqku">⚡ ACCESO INMEDIATO</span>
            <span className="text-blue-300/40" data-id="element-124" data-mp="2mipzftvrszhr">|</span>
            <span data-id="element-125" data-mp="2mg3r3s122fyh">🔒 PAGO SEGURO</span>
            <span className="text-blue-300/40" data-id="element-126" data-mp="28iyoy34gq18i">|</span>
            <span data-id="element-127" data-mp="28k9rjzm692y1">💻 100% DIGITAL</span>
          </div>
        </div>
      </section>

      {/* PROBLEM IDENTIFICATION */}
      <section className="bg-[#07172b] px-4 py-10 text-white md:py-14" style={{
      paddingTop: "16px",
      paddingBottom: "10px"
    }} data-id="element-128" data-mp="2djcvthubv54y">
        <div className="container mx-auto max-w-3xl" data-id="element-129" data-mp="357ms5tbirjxv">
          <div className="text-center" data-id="element-130" data-mp="35aiudtuegus4">
            <p className="inline-flex rounded-full border border-orange-500/25 bg-orange-500/10 px-3 py-1 text-[10px] font-black tracking-[0.12em] text-orange-200" data-id="element-131" data-mp="2rg89jnlixkrc">
              EL PROBLEMA NO ES TU EXPERIENCIA
            </p>
            <h2 className="mt-3 text-3xl font-black leading-tight tracking-tight md:text-5xl" data-id="element-132" data-mp="3k4dsc4t0ozoj">
              ¿CUÁNTO TE LIMITA <span className="text-orange-400" data-id="element-133" data-mp="3oyrwavlqwf4t">NO SABER PLC</span>?
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-blue-100/80 md:text-base" data-id="element-134" data-mp="1ow0hhmy30q2c">
              Ya sabés de motores, contactores y tableros. Pero cuando aparece un PLC, ¿qué pasa?
            </p>
          </div>

          <div className="mt-6 space-y-3" data-id="element-135" data-mp="1a5jg23lg605g">
            <article className="flex items-start gap-3 rounded-xl border border-blue-300/15 bg-blue-900/35 p-4" data-id="element-136" data-mp="1a6ktuftul0qv">
              <AlertTriangle className="mt-0.5 h-6 w-6 flex-shrink-0 text-orange-400" data-id="element-137" data-mp="27srvphyroezj" />
              <div data-id="element-138" data-mp="22vi90a2rihfe">
                <h3 className="text-sm font-black leading-snug text-white" data-id="element-139" data-mp="22v01ghanggs6">EL TABLERO TIENE UN PLC.</h3>
                <p className="mt-1 text-xs leading-relaxed text-blue-100/65" data-id="element-140" data-mp="1xx6yry8k24wn">
                  Entendés las conexiones, pero no la lógica que controla el sistema.
                </p>
              </div>
            </article>

            <article className="flex items-start gap-3 rounded-xl border border-blue-300/15 bg-blue-900/35 p-4" data-id="element-141" data-mp="iedah7myja8s">
              <BookOpen className="mt-0.5 h-6 w-6 flex-shrink-0 text-orange-400" data-id="element-142" data-mp="2u28phwguc6rx" />
              <div data-id="element-143" data-mp="2y3afdmovb6h8">
                <h3 className="text-sm font-black leading-snug text-white" data-id="element-144" data-mp="2y2sluxtlb6d0">TE PIDEN LADDER O S7-1200.</h3>
                <p className="mt-1 text-xs leading-relaxed text-blue-100/65" data-id="element-145" data-mp="33255l9p5zxbd">
                  Tenés experiencia eléctrica, pero te falta práctica en programación.
                </p>
              </div>
            </article>

            <article className="flex items-start gap-3 rounded-xl border border-blue-300/15 bg-blue-900/35 p-4" data-id="element-146" data-mp="3cror6n49i59x">
              <Target className="mt-0.5 h-6 w-6 flex-shrink-0 text-orange-400" data-id="element-147" data-mp="3hpgjvjcqdvf6" />
              <div data-id="element-148" data-mp="3lu6b2uwg2ltr">
                <h3 className="text-sm font-black leading-snug text-white" data-id="element-149" data-mp="3lqsans7ccmoy">APARECEN TRABAJOS DE MAYOR NIVEL.</h3>
                <p className="mt-1 text-xs leading-relaxed text-blue-100/65" data-id="element-150" data-mp="3qplo5kmmodp4">
                  Pero no te sentís preparado para asumir tareas de automatización.
                </p>
              </div>
            </article>
          </div>

          <div className="mt-7 border-t border-blue-300/20 pt-6 text-center" data-id="element-151" data-mp="1hzgqov95nido">
            <p className="text-base font-black leading-tight text-white md:text-xl" data-id="element-152" data-mp="1mq6xmcx8btah">
              YA TENÉS LA BASE ELÉCTRICA.
            </p>
            <p className="mt-1 text-base font-black leading-tight text-orange-400 md:text-xl" data-id="element-153" data-mp="182vjc39nxv6o">
              AHORA SUMÁ PROGRAMACIÓN.
            </p>
          </div>
        </div>
      </section>

      {/* POST-HERO - ECOSISTEMA TÉCNICO */}
      <section id="post-hero-section" className="px-4 py-9 md:py-12" style={{
      background: '#0a1929',
      paddingTop: "0px"
    }} data-id="element-154" data-mp="20of2r84f60wq">
        <div className="container mx-auto max-w-4xl" data-id="element-155" data-mp="1rqbi0e19d85z">
          <div className="text-center mb-6" data-id="element-156" data-mp="1rrw89rgji4y1">
            <h2 className="text-2xl md:text-4xl font-extrabold text-white uppercase tracking-wide mb-2" data-id="element-157" data-mp="1vq3qkqqnjebc">
              UN ECOSISTEMA TÉCNICO COMPLETO EN TUS MANOS
            </h2>
            <div className="h-1 w-24 bg-orange-500 mx-auto rounded-full" data-id="element-158" data-mp="kchs2eywk0d5"></div>
          </div>

          <div className="grid grid-cols-2 gap-3 md:gap-4" data-id="element-159" data-mp="cs78skl49l7y">
            {ecosystemItems.map((item, i) => <article key={item.title} className={`rounded-xl border border-blue-300/15 bg-blue-900/35 p-3 ${i === ecosystemItems.length - 1 ? 'col-span-2 flex items-start gap-3 md:px-5' : ''}`} data-id="element-160" data-mp="7tmfl54a81t0" data-mpi={i}>
                <div className={`mb-2 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-blue-950/60 ${i === ecosystemItems.length - 1 ? 'md:mb-0' : ''}`} data-id="element-161" data-mp="hrtjondh2pdl">
                  <item.icon className="h-5 w-5 text-orange-400" data-id="element-162" data-mp="3q593koxh6gkz" />
                </div>
                <div data-id="element-163" data-mp="3la3lk1771m26">
                  <h3 className="text-[13px] font-black leading-tight text-white uppercase md:text-sm" data-id="element-164" data-mp="3rsi15pdvdx2">
                    {item.title}
                  </h3>
                  <p className="mt-1 text-[11px] leading-snug text-blue-100/65 md:text-xs" data-id="element-165" data-mp="3v4vkp9gfcgz2">
                    {item.desc}
                  </p>
                </div>
              </article>)}
          </div>
        </div>
      </section>

      {/* EL PROBLEMA REAL - 2x2 Grid */}
      

      {/* PLC LEARNING OBJECTION */}
      <section className="bg-[#0a1120] px-4 py-10 text-white md:py-14" style={{
      paddingTop: "0px",
      paddingBottom: "18px"
    }} data-id="element-166" data-mp="3s3u0j4ndh1q0">
        <div className="container mx-auto max-w-3xl text-center" data-id="element-167" data-mp="4scbiogowgz9">
          <div className="rounded-xl border border-blue-300/20 bg-blue-900/35 px-5 py-6 md:px-8 md:py-8" data-id="element-168" data-mp="4v6uebk21tqk">
            <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-orange-500/10" data-id="element-169" data-mp="3972beel5fse1">
              <Lightbulb className="h-6 w-6 text-orange-400" data-id="element-170" data-mp="3d8e10k40wdm8" />
            </div>
            <h2 className="mt-4 text-2xl font-black leading-tight md:text-4xl" data-id="element-171" data-mp="3dah1jy2sxp57">
              NO NECESITÁS SER UN GENIO PARA <span className="text-orange-400" data-id="element-172" data-mp="3n6k4351kfpso">APRENDER PLC</span>.
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-blue-100/80 md:text-base" data-id="element-173" data-mp="3n4h5vxq9eqcv">
              Si ya trabajás con motores, contactores y tableros, tenés una base que podés aprovechar.
            </p>
            <p className="mt-3 text-base font-bold leading-relaxed text-white md:text-lg" data-id="element-174" data-mp="2mydlrzaqn6nj">
              La diferencia está en aprender a llevar esa lógica a un programa.
            </p>
            <p className="mt-4 border-t border-blue-300/15 pt-4 text-sm leading-relaxed text-blue-100/80 md:text-base" data-id="element-175" data-mp="2d8u3ovuungtq">
              No necesitás empezar de cero en electricidad. Necesitás <span className="font-black text-orange-400" data-id="element-176" data-mp="287o9etljjli3">un método para empezar con PLC</span>.
            </p>
          </div>
          <div className="mt-5 flex flex-col items-center gap-1 text-orange-400" data-id="element-177" data-mp="1z9cbe6ichlfm">
            <ChevronDown className="h-5 w-5" aria-hidden="true" data-id="element-178" data-mp="1zbf9zexkcmvy" />
            <p className="text-[11px] font-black tracking-[0.12em]" data-id="element-179" data-mp="1uahsz5oahyw1">CONOCÉ EL MÉTODO PLC MASTER</p>
          </div>
        </div>
      </section>

      {/* SOLUTION SECTION */}
      <section id="solution" className="relative overflow-hidden bg-gradient-to-b from-blue-900 to-blue-800 px-4 py-10 text-white md:py-12" data-id="element-180" data-mp="3oo8xxrvobul5">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/circuit-board.png')] opacity-10" style={{
        paddingBottom: "2px"
      }} data-id="element-181" data-mp="3jmte2358fu4c"></div>
        <div className="container relative z-10 mx-auto max-w-4xl" data-id="element-182" data-mp="isn1svwug0f">
          <div className="mx-auto max-w-3xl text-center" data-id="element-183" data-mp="3hs7fljb0uun7">
            <p className="text-[10px] font-black tracking-[0.14em] text-orange-300" data-id="element-184" data-mp="3htsaja190fll">ASÍ FUNCIONA PLC MASTER</p>
            <h2 className="mt-2 text-3xl font-black leading-tight md:text-5xl" data-id="element-185" data-mp="38z48dsqyze10">
              NO ES SOLO APRENDER PLC. ES <span className="text-orange-400" data-id="element-186" data-mp="3dpkmzg6j15oy">APRENDER A PROGRAMAR</span>.
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-blue-100/85 md:text-base" data-id="element-187" data-mp="2z0y454hoxvsz">
              Un sistema pensado para que avances desde tu experiencia eléctrica hasta comprender, practicar y desarrollar lógica de automatización industrial.
            </p>
          </div>

          <div className="mx-auto mt-6 max-w-3xl space-y-2.5" data-id="element-188" data-mp="2u1tw4h6i6448">
            {[{
            number: '01',
            icon: BookOpen,
            title: 'APRENDÉS CON UN ORDEN',
            desc: 'De contactos y circuitos a Ladder y bloques de función, sin saltar directamente a lo avanzado.'
          }, {
            number: '02',
            icon: Cpu,
            title: 'TRABAJÁS CON SOFTWARE INDUSTRIAL',
            desc: 'Conocé los entornos de programación como TIA Portal, Studio 5000 y RSLogix.'
          }, {
            number: '03',
            icon: Settings,
            title: 'PROBÁS ANTES DE IR A PLANTA',
            desc: 'Usá simuladores y máquinas virtuales para practicar la lógica y detectar errores.'
          }, {
            number: '04',
            icon: CircuitBoard,
            title: 'CONECTÁS LA TEORÍA CON LA INDUSTRIA',
            desc: 'Relacioná la programación con motores, señales, fallas y sistemas reales de control.'
          }].map(item => <article key={item.number} className="flex items-start gap-3 rounded-xl border border-blue-300/15 bg-blue-950/30 px-3 py-3.5 text-left md:px-4" data-id="element-189" data-mp="3d0socmxrhl31">
                <span className="pt-0.5 text-xs font-black text-orange-400" data-id="element-190" data-mp="25rfedmmi8me1">{item.number}</span>
                <item.icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-orange-400" data-id="element-191" data-mp="29t0xvqtjnyx7" />
                <div data-id="element-192" data-mp="2equ3oiqxqqwy">
                  <h3 className="text-[13px] font-black leading-tight text-white md:text-sm" data-id="element-193" data-mp="2eqam535764jg">{item.title}</h3>
                  <p className="mt-1 text-[11px] leading-relaxed text-blue-100/70 md:text-xs" data-id="element-194" data-mp="2jjdghdhsti0p">{item.desc}</p>
                </div>
              </article>)}
          </div>

          <figure className="mx-auto mt-5 w-full max-w-sm overflow-hidden rounded-lg border border-blue-300/20 bg-blue-950/30 p-1.5" data-id="element-195" data-mp="2tf71zmdhwlum">
            <img src="https://cdn.magicpatterns.com/uploads/btg4iHmVzrFWBqdRhRw1bo/Electrician_learning_PLC_program…_20261017102833.webp" alt="Técnico practicando programación PLC con el manual y simulador" className="block w-full rounded object-cover" data-id="element-196" data-mp="2xid54l5b9wwv" />
          </figure>
        </div>
      </section>

      {/* BONUSES - 4 CARDS WITH SLIDER */}
      <section id="bonuses" className="py-16 px-4 bg-gradient-to-b from-white to-blue-50 pt-[20px] pb-[25px]" data-id="element-197" data-mp="10iuspc50thh7">
        <div className="container mx-auto max-w-6xl" data-id="element-198" data-mp="vs4jtoxdywhr">
          <div className="text-center mb-10" data-reveal data-id="element-199" data-mp="vqbf4f6c2mce">
            <h2 className="text-2xl md:text-4xl font-bold text-blue-900 mb-4 tracking-tight relative inline-block uppercase" data-id="element-200" data-mp="qucqqrl094bz">
              ADEMÁS, ASEGURÁ ESTOS{' '}
              <span className="text-orange-500 bg-orange-100 px-2 rounded" data-id="element-201" data-mp="1jhqi8a5xp7op">
                4
              </span>{' '}
              BONOS EXCLUSIVOS DE LANZAMIENTO
            </h2>
            <p className="text-lg text-gray-700 max-w-2xl mx-auto mb-6 mt-2" data-id="element-202" data-mp="19jsvjr3q3iy7">
              Complementos estratégicos para tu crecimiento como especialista.
            </p>
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-6 py-3 rounded-full font-bold text-sm uppercase tracking-wide" data-id="element-203" data-mp="15hxxcvh39lon">
              <Clock className="w-4 h-4" data-id="element-204" data-mp="3q86skkrsnnt9" />
              GRATIS POR TIEMPO LIMITADO
            </div>
          </div>

          {/* Horizontal Carousel */}
          <div className="relative max-w-5xl mx-auto" data-id="element-205" data-mp="1t9wwdyprmi5w">
            <div ref={bonusScrollRef} className="flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory hide-scrollbar" style={{
            scrollBehavior: 'smooth',
            WebkitOverflowScrolling: 'touch'
          }} onScroll={e => {
            const scrollLeft = e.currentTarget.scrollLeft;
            const maxScroll = e.currentTarget.scrollWidth - e.currentTarget.clientWidth;
            if (maxScroll > 0) {
              const progress = scrollLeft / maxScroll;
              const newIndex = Math.round(progress * (bonuses.length - 1));
              if (newIndex !== activeBonus) {
                setActiveBonus(newIndex);
              }
            }
          }} data-id="element-206" data-mp="1y7ycqbvt43do">
              {bonuses.map((bonus, i) => <div key={i} onClick={scrollToPurchase} className="cursor-pointer group flex-shrink-0 w-[300px] md:w-[340px] snap-center" data-id="element-207" data-mp="2vtvs40ew3sle" data-mpi={i}>
                  <div className="bg-white rounded-xl overflow-hidden border-2 border-gray-100 transition-all transform hover:-translate-y-2 h-full flex flex-col shadow-lg hover:shadow-2xl" data-id="element-208" data-mp="30n6wzw055zao">
                    <div className="bg-gradient-to-r from-orange-600 to-orange-500 text-white px-4 py-2 text-center font-bold text-sm uppercase" data-id="element-209" data-mp="35wrlzsk4ez2y">
                      {bonus.label}
                    </div>
                    <div className="p-5 flex flex-col flex-grow" data-id="element-210" data-mp="2r1bnnbcich0r">
                      <div className="aspect-[3/4] mb-4 bg-gray-100 rounded-lg overflow-hidden relative group-hover:scale-[1.02] transition-transform duration-500" data-id="element-211" data-mp="2r1tpqoluppgl">
                        <img src={bonus.img} alt={bonus.title} className="w-full h-full object-cover" data-id="element-212" data-mp="30zrerebjbqa9" />
                      </div>
                      <h3 className="font-bold text-lg text-gray-900 mb-3 leading-tight uppercase" data-id="element-213" data-mp="2vytpkhs3xvz9">
                        {bonus.title}
                      </h3>
                      <p className="text-sm text-gray-700 mb-4 leading-relaxed flex-grow" data-id="element-214" data-mp="bayw1k6l84b6">
                        {bonus.desc}
                      </p>
                      <p className="text-sm text-gray-600 mb-2" data-id="element-215" data-mp="23htfctfilp47">
                        Valor:{' '}
                        <span className="line-through text-gray-400" data-id="element-216" data-mp="1yiyvdo5t9uii">
                          {bonus.value}
                        </span>
                      </p>
                      <div className="bg-green-50 border-green-200 border rounded-lg px-3 py-2 mt-auto flex items-center gap-2" data-id="element-217" data-mp="28bxnhg0io36v">
                        <Check className="w-4 h-4 text-green-600" data-id="element-218" data-mp="1kn4n97cv396g" />
                        <p className="text-sm font-bold text-green-700" data-id="element-219" data-mp="1fn8p3oquibyt">
                          Incluido sin costo
                        </p>
                      </div>
                    </div>
                  </div>
                </div>)}
            </div>

            {/* Slider Progress Bar */}
            <div className="mt-6 flex flex-col items-center gap-3" data-id="element-220" data-mp="34zkg12ugz1f1">
              <div className="w-full max-w-xs h-1.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-221" data-mp="30zthue2m5j0h">
                <div className="h-full bg-orange-500 rounded-full transition-all duration-300 ease-out" style={{
                width: `${(activeBonus + 1) / bonuses.length * 100}%`
              }} data-id="element-222" data-mp="38i9p5jn3vgdq"></div>
              </div>
              <p className="text-sm text-gray-400 font-medium" data-id="element-223" data-mp="2ylwx0q77lal3">
                Desliza para ver todos los bonos
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* LO QUE CAMBIA */}
      <section id="benefits-section" className="bg-gradient-to-b from-gray-50 to-white px-4 py-10 md:py-14" style={{
      paddingBottom: "13px"
    }} data-id="element-224" data-mp="3ra2i5e26d1lg">
        <div className="container mx-auto max-w-6xl" data-id="element-225" data-mp="3hjr6148c7m3h">
          <div className="mx-auto max-w-2xl text-center" data-id="element-226" data-mp="3hj7ldfrayjlr">
            <h2 className="text-3xl font-black leading-tight text-blue-900 md:text-4xl" data-id="element-227" data-mp="3mhsjn9mzut7k">
              LO QUE CAMBIA CUANDO APRENDÉS PLC
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-gray-600 md:text-base" data-id="element-228" data-mp="e0o4f9e2r069">
              No empezás de cero. Llevás tu experiencia eléctrica a otro nivel.
            </p>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4" data-id="element-229" data-mp="snd8loj9u1iz">
            {[{
            icon: Shield,
            title: 'MÁS AUTONOMÍA',
            desc: 'Entendé la lógica del sistema y diagnosticá con mayor criterio.'
          }, {
            icon: Target,
            title: 'MEJOR DIAGNÓSTICO',
            desc: 'Interpretá programas y analizá fallas sin improvisar.'
          }, {
            icon: TrendingUp,
            title: 'MÁS OPORTUNIDADES',
            desc: 'Preparáte para trabajos de automatización de mayor nivel técnico.'
          }, {
            icon: Brain,
            title: 'PERFIL MÁS COMPLETO',
            desc: 'Sumá Ladder, LOGO! V8 y S7-1200 a tu experiencia eléctrica.'
          }].map(card => <article key={card.title} className="flex min-w-0 flex-col rounded-xl border border-blue-100 bg-white p-3.5 shadow-sm md:p-4" data-id="element-230" data-mp="3s65wtlnewurb">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-orange-50" data-id="element-231" data-mp="3i6orj5c2oivv">
                  <card.icon className="h-5 w-5 text-orange-500" data-id="element-232" data-mp="1xommf7yfs46y" />
                </div>
                <h3 className="mt-3 text-[13px] font-black leading-tight text-blue-950 md:text-sm" data-id="element-233" data-mp="22nh3a4cufiof">
                  {card.title}
                </h3>
                <p className="mt-1.5 text-[11px] leading-snug text-gray-600 md:text-xs" data-id="element-234" data-mp="27nvdnt1x34pm">
                  {card.desc}
                </p>
              </article>)}
          </div>

          <div className="mx-auto mt-5 flex max-w-4xl flex-col gap-1 rounded-xl border border-blue-200 bg-blue-950 px-4 py-3 text-center text-white md:flex-row md:items-center md:justify-center md:gap-3 md:text-left" data-id="element-235" data-mp="1eyo47z0xcupl">
            <p className="text-sm font-black" data-id="element-236" data-mp="1nxro357x1e55">TU EXPERIENCIA ELÉCTRICA YA TIENE VALOR.</p>
            <p className="text-xs leading-relaxed text-blue-100/85 md:text-sm" data-id="element-237" data-mp="1sqbbsv0bhpno">
              La programación PLC puede ayudarte a ampliarlo y acceder a nuevas oportunidades profesionales.
            </p>
          </div>
        </div>
      </section>

      {/* PACK SECTION */}
      <section className="bg-white px-4 py-10 md:py-12" style={{
      paddingTop: "0px",
      paddingBottom: "0px"
    }} data-id="element-238" data-mp="h3u6g7ykqv4x">
        <div className="mx-auto max-w-4xl" data-id="element-239" data-mp="c622ugokhkkw">
          <div className="mb-5 text-center" data-id="element-240" data-mp="c73qdkmrrzji">
            <p className="text-[11px] font-black tracking-[0.14em] text-orange-600" data-id="element-241" data-mp="quov1izam6tj">MATERIAL TÉCNICO DIGITAL</p>
            <h2 className="mt-1 text-3xl font-black leading-tight text-[#0B1C3B] md:text-4xl" data-id="element-242" data-mp="m36pehmbri28">TODO LO QUE RECIBÍS CON PLC MASTER</h2>
          </div>

          <img src="https://cdn.magicpatterns.com/uploads/rYBWMxxTLWCHMEE8QQbgPe/Edit_e-commerce_product_photograph_20261024140318.webp" className="block h-auto w-full rounded-2xl border border-blue-100 object-contain" alt="Manual PLC MASTER, Variadores de Frecuencia y bonos técnicos incluidos" data-id="element-243" data-mp="zwy21yvcri26" />

          <div ref={includedContentRef} className={`included-content-summary mt-3 rounded-xl border border-blue-100 bg-sky-50 px-4 py-3 ${includedContentVisible ? 'included-content-visible' : ''}`} data-id="element-244" data-mp="14rv7kvtfb9ab">
            <div className="flex items-center gap-2 text-[#0B1C3B]" data-id="element-245" data-mp="36ndxl3cjztmo">
              <Zap className="h-4 w-4 flex-shrink-0 text-orange-500" aria-hidden="true" data-id="element-246" data-mp="1m4lfu7gxbd9w" />
              <p className="text-sm font-black leading-tight" data-id="element-247" data-mp="1r120growz9i8">PLC MASTER + 4 BONOS TÉCNICOS</p>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-x-5 gap-y-2 sm:grid-cols-2" data-id="element-248" data-mp="1ccxriffnyccn">
              {['Curso completo de SCADA', 'Módulo extra de PLC', 'Control de procesos', 'Variadores y Sensores'].map((bonus, index) => <div key={bonus} className="included-content-item" style={{
              '--included-delay': `${index * 170}ms`
            } as React.CSSProperties} data-id="element-249" data-mp="13bhgc5h0y67d" data-mpi={index}>
                  <svg className="included-check" viewBox="0 0 20 20" aria-hidden="true" data-id="element-250" data-mp="tfegj69najnl"><path d="M4 10.5 8 14l8-8" data-id="element-251" data-mp="ydoc3hza7m9q" /></svg>
                  <span data-id="element-252" data-mp="yehb1ws017hb">{bonus}</span>
                </div>)}
            </div>
            <p className="mt-3 border-t border-blue-100 pt-2 text-[11px] font-medium leading-snug text-slate-500" data-id="element-253" data-mp="2nscp9lfp588t"><Zap className="mr-1 inline h-3 w-3 text-orange-500" aria-hidden="true" data-id="element-254" data-mp="2srz1psnpakgk" /> Manual de Variadores de Frecuencia disponible en el pack superior.</p>
          </div>
        </div>
      </section>

      {/* PURCHASE SECTION */}
      <section id="compra" className="bg-sky-50 px-4 py-12 md:py-16" style={{
      paddingTop: "0px",
      paddingBottom: "13px"
    }} data-id="element-255" data-mp="2m9vxvi8fkvnf">
        <div className="mx-auto max-w-5xl" data-id="element-256" data-mp="2he727pzj7ywe">
          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_18px_44px_rgba(15,37,73,0.14)]" data-id="element-257" data-mp="2ceccvqvenhdg">
            <div className="grid items-center gap-8 p-5 md:grid-cols-[1.05fr_0.95fr] md:gap-12 md:p-10" style={{
            gap: "11px"
          }} data-id="element-258" data-mp="3a09od4bdamfw">
              <div className="order-1 flex justify-center md:order-2" data-id="element-259" data-mp="30aykbw3c87yv">
                <img src="https://cdn.magicpatterns.com/uploads/hmqNJ8pT1CBLWsJ2sPunM5/Pack_PLC_Master_y_Variadores_Industriales.webp" alt="Pack PLC MASTER y Variadores de Frecuencia" className="h-auto w-full max-w-[540px] object-contain" data-id="element-260" data-mp="308vj0fwqiseu" />
              </div>
              <div className="order-2 md:order-1" data-id="element-261" data-mp="3o00xorqxvenk">
                <p className="text-xs font-black tracking-[0.14em] text-orange-600" data-id="element-262" data-mp="42glz4n07vw">FORMACIÓN TÉCNICA DIGITAL</p>
                <h2 className="mt-2 text-3xl font-black leading-tight text-[#0B1C3B] md:text-5xl" data-id="element-263" data-mp="45ebaj2dbjay">PLC MASTER + VARIADORES DE FRECUENCIA</h2>
                <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-600 md:text-base" data-id="element-264" data-mp="93z622yupas0">Dos manuales técnicos para avanzar en programación PLC, control de motores y automatización industrial.</p>
                <div className="mt-6 border-t border-slate-200 pt-5" style={{
                paddingTop: "0px"
              }} data-id="element-265" data-mp="j2f0aet6d05q">
                  <p className="text-sm font-black text-[#0B1C3B]" data-id="element-266" data-mp="n2fi89fhfthj">QUÉ INCLUYE</p>
                  <ul className="mt-3 space-y-2.5 text-sm font-medium leading-snug text-slate-700" data-id="element-267" data-mp="rw0g81h2sngq">
                    {['Manual PLC MASTER DESDE CERO', 'Manual VARIADORES DE FRECUENCIA', 'Programación Ladder, LOGO! V8 y S7-1200', 'Entradas, salidas, temporizadores y contadores', 'Fundamentos de HMI y SCADA', 'Control de velocidad y configuración de variadores'].map(item => <li key={item} className="flex items-start gap-2" data-id="element-268" data-mp="1s2oefcyc2ss7"><Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" aria-hidden="true" data-id="element-269" data-mp="1s5kbz069rfg4" />{item}</li>)}
                  </ul>
                </div>
                <div className="mt-4 rounded-lg border border-blue-100 bg-sky-50 px-3 py-2.5" data-id="element-270" data-mp="21332ul6lgnzx">
                  <p className="flex items-center gap-1.5 text-xs font-black tracking-wide text-[#0B1C3B]" data-id="element-271" data-mp="1w64943quej29"><Download className="h-4 w-4 flex-shrink-0 text-orange-500" aria-hidden="true" data-id="element-272" data-mp="18ep23xo4fdko" /> FORMATO 100% DIGITAL</p>
                  <p className="mt-1 text-[11px] leading-relaxed text-slate-600" data-id="element-273" data-mp="14f6fmxgniufq">Manuales en PDF + clases en video + recursos de simulación PLC. Acceso inmediato, sin envío físico.</p>
                </div>
                <div className="mt-7 border-t border-slate-200 pt-5" data-id="element-274" data-mp="1dd0cc9ibvq3f">
                  <p className="text-xs font-bold tracking-wide text-slate-500" data-id="element-275" data-mp="po7da46et24l">INVERSIÓN ÚNICA</p>
                  <p className="mt-1 text-5xl font-black tracking-tight text-emerald-600 md:text-6xl" data-id="element-276" data-mp="kqnzf5dmird6">AR$ 19.990</p>
                  <button type="button" onClick={handlePurchaseClick} className="mt-5 flex min-h-[68px] w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-5 text-center text-base font-black text-white shadow-[0_7px_0_#167a45,0_16px_26px_rgba(22,163,74,0.26)] transition-[transform,box-shadow,background-color] duration-180 ease-out hover:bg-emerald-500 active:translate-y-[2px] active:shadow-[0_3px_0_#167a45,0_8px_14px_rgba(22,163,74,0.2)] md:text-lg" data-id="element-277" data-mp="2wcg1thvgelwz"><Zap className="h-6 w-6 flex-shrink-0" aria-hidden="true" data-id="element-278" data-mp="3k2tunalv4osm" /> ¡SÍ! QUIERO PLC MASTER + VARIADORES</button>
                  <img src="https://cdn.magicpatterns.com/uploads/hgUps8NNywDJCXoWVVgxqr/capture_260410_163646.png" alt="Pago seguro con Visa, Mastercard, American Express y Mercado Pago" className="mx-auto mt-4 h-auto w-full max-w-[360px] object-contain" data-id="element-279" data-mp="363kjwqgfemw1" />
                  <p className="mt-3 flex items-center justify-center gap-2 text-center text-[11px] font-bold tracking-[0.08em] text-slate-500" data-id="element-280" data-mp="1upyq2rzmfx46"><Download className="h-4 w-4 text-[#0B1C3B]" aria-hidden="true" data-id="element-281" data-mp="2842x8b77dypd" /> ACCESO DIGITAL <span aria-hidden="true" data-id="element-282" data-mp="2866y0k9t2hlf">·</span> <Lock className="h-4 w-4 text-[#0B1C3B]" aria-hidden="true" data-id="element-283" data-mp="2d24h2ycge21w" /> PAGO SEGURO</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BONO EXTRA EXCLUSIVO - ACCESO VITALICIO */}
      <section className="py-8 px-4 bg-white pt-[0px] pb-[20px]" data-id="element-284" data-mp="2mz0rj9lwwy93">
        <div className="container mx-auto max-w-4xl" data-id="element-285" data-mp="1penyinezplo5">
          <div className="bg-orange-50 rounded-2xl overflow-hidden shadow-lg border border-orange-200 relative" data-id="element-286" data-mp="1ucfrlkrdfdtx">
            {/* Decorative background elements */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/5 blur-2xl rounded-full -mr-16 -mt-16" data-id="element-287" data-mp="1za7rp2266697"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-blue-500/5 blur-2xl rounded-full -ml-16 -mb-16" data-id="element-288" data-mp="2442a8261gf6p"></div>

            <div className="relative z-10 flex flex-col md:flex-row items-center gap-6 p-5 md:p-6" style={{
            paddingTop: "5px",
            paddingBottom: "6px",
            gap: "4px",
            width: "360px",
            height: "285px"
          }} data-id="element-289" data-mp="1blom4uai87kt">
              {/* Image Column */}
              <div className="w-full md:w-1/4 flex justify-center" data-id="element-290" data-mp="3fv9rf7whgq8h">
                <div className="relative group" data-id="element-291" data-mp="3fxmc3stmlep8">
                  <div className="absolute inset-0 bg-orange-500/10 blur-xl rounded-full group-hover:bg-orange-500/20 transition-colors duration-500" data-id="element-292" data-mp="3avdj4wgr6cn4"></div>
                  <img src="https://cdn.magicpatterns.com/uploads/w8PJr1f91cKGQkijsD98DU/Gemini_Generated_Image_ajg2w8ajg2w8ajg2.png" alt="Acceso Vitalicio" className="w-full max-w-[120px] relative z-10 transform group-hover:scale-105 transition-transform duration-500 drop-shadow-[0_0_10px_rgba(255,165,0,0.1)]" style={{
                  width: "107px",
                  height: "91px"
                }} data-id="element-293" data-mp="35z6q249ggvml" />
                </div>
              </div>

              {/* Content Column */}
              <div className="w-full md:w-3/4 text-center md:text-left" style={{
              width: "340px",
              height: "188px"
            }} data-id="element-294" data-mp="2i9ueixqnobh4">
                <div className="inline-block bg-orange-500 text-white font-black px-3 py-1 rounded-md text-[10px] uppercase tracking-[0.15em] mb-2 shadow-md shadow-orange-500/10" data-id="element-295" data-mp="2dk9tsanx6rhi">
                  BONO EXTRA EXCLUSIVO
                </div>
                <h3 className="text-xl md:text-2xl font-black text-gray-900 mb-2 leading-tight uppercase tracking-tight" data-id="element-296" data-mp="23n58se5t922a">
                  ACCESO <span className="text-orange-500" data-id="element-297" data-mp="2w9gf85g3lx70">VITALICIO</span>
                </h3>
                <p className="text-gray-600 text-sm md:text-base leading-relaxed mb-4 font-medium" data-id="element-298" data-mp="2iibdmxt66uu1">
                  Olvidate de las mensualidades. Con un solo pago tenés acceso
                  de por vida a todo el material y a las futuras actualizaciones
                  sin pagar un centavo más.
                </p>
                <div className="inline-flex items-center gap-2 bg-green-100 border border-green-200 px-4 py-1.5 rounded-xl" data-id="element-299" data-mp="3f0he6th7xsqk">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" data-id="element-300" data-mp="13jxn0561u91x"></div>
                  <span className="text-green-700 font-black text-xs uppercase tracking-wider" data-id="element-301" data-mp="yl47y8b469zn">
                    INCLUIDO GRATIS
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTENT PREVIEW */}
      <section className="py-16 px-4 bg-white" style={{
      paddingTop: "0px",
      paddingBottom: "23px"
    }} data-id="element-302" data-mp="kll61x8wbuvs">
        <div className="container mx-auto max-w-6xl" data-id="element-303" data-mp="fol6842n3od4">
          <div className="text-center mb-12" data-id="element-304" data-mp="ukanxq0slfc1">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" data-id="element-305" data-mp="um58v4c2d7g4">
              Así es el material por dentro
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-306" data-mp="25zpjcpdcrz51">
              Ejemplos reales de las páginas que vas a usar en el trabajo diario
            </p>
          </div>

          <div className="space-y-16" data-id="element-307" data-mp="212qog4lv7o5s">
            <div className="flex justify-center" data-id="element-308" data-mp="ak28yhz56ort">
              <div className="w-full max-w-2xl bg-gray-900 rounded-xl overflow-hidden shadow-2xl border-4 border-gray-100" data-id="element-309" data-mp="aldccgooduie">
                <video src="https://cdn.magicpatterns.com/uploads/au6q7KQ39reudghcBDBGdL/WhatsApp Video 2026-02-25 at 13.33.35.mp4" className="w-full" autoPlay loop muted playsInline data-id="element-310" data-mp="jk75fx2ss2r7" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-100 to-gray-50" style={{
      paddingTop: "7px",
      paddingBottom: "0px"
    }} data-id="element-311" data-mp="tgk83kgblqy3">
        <div className="container mx-auto max-w-2xl" data-reveal data-id="element-312" data-mp="13998nkac1ken">
          <div className="text-center mb-10" data-id="element-313" data-mp="179jnlmg3rl5n">
            <div className="flex items-center justify-center gap-1 mb-3" data-id="element-314" data-mp="1799xlyv25dzy">
              {[1, 2, 3, 4, 5].map(s => <Star key={s} className="w-5 h-5 fill-orange-400 text-orange-400" data-id="element-315" data-mp="1caftg5k0lhdp" />)}
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 uppercase tracking-tight" data-id="element-316" data-mp="1h8h72ayo8ohs">
              MÁS DE 3.200 ALUMNOS RESPALDAN NUESTRA COMUNIDAD
            </h2>
            <p className="text-sm text-gray-500 mt-2 font-medium" data-id="element-317" data-mp="1mbrruzv0q68w">
              4,9/5 — 487 reseñas verificadas
            </p>
          </div>

          {/* WhatsApp-style chat bubbles */}
          <div className="space-y-4 mb-8" data-id="element-318" data-mp="1dcwkvlk5gs4j">
            {allChatReviews.slice(0, visibleTestimonials).map((t, i) => <TestimonialItem key={i} t={t} data-id="element-319" data-mp="2a1cd5pen9flc" data-mpi={i} />)}
          </div>

          {visibleTestimonials < allChatReviews.length && <div className="text-center" data-id="element-320" data-mp="254lxh36r4m0g">
              <button onClick={showMoreTestimonials} className="btn-shine bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 font-medium py-3 px-8 rounded-full transition-all shadow-sm hover:shadow-md text-sm" data-id="element-321" data-mp="255ndzn6je0pk">
                Cargar 5 comentarios más
              </button>
            </div>}

          <ReviewForm onSubmitReview={handleSubmitReview} data-id="element-322" data-mp="2o26r0nkgo8re" />
        </div>
      </section>

      <section ref={finalComboRef} className="bg-white px-4 py-9 md:py-12" aria-labelledby="final-offer-title" data-id="element-323" data-mp="2o0c1evy8vsgz">
        <div className={`mx-auto max-w-xl overflow-hidden rounded-2xl border border-[#D6A44B]/80 bg-[#0B1C3B] px-5 py-6 text-center shadow-[0_10px_24px_rgba(11,28,59,0.18)] md:px-8 ${finalComboVisible ? 'final-combo-enter' : 'opacity-0'}`} data-id="element-324" data-mp="r3ptxv87z1z9">
          <span className="inline-flex items-center gap-1 rounded-full border border-[#D6A44B]/70 bg-[#D6A44B]/10 px-3 py-1 text-[10px] font-black tracking-[0.12em] text-[#E8C272]" data-id="element-325" data-mp="hcliv6lo019p"><Zap className="h-3 w-3" aria-hidden="true" data-id="element-326" data-mp="8dregu23e6ll" /> ANTES DE IRTE...</span>
          <h2 id="final-offer-title" className="mx-auto mt-4 max-w-md text-2xl font-black leading-tight text-white md:text-3xl" data-id="element-327" data-mp="3pld1x9m8o92h">¡TU PRÓXIMO PASO EN <span className="text-orange-400" data-id="element-328" data-mp="3ulhit0r275t8">AUTOMATIZACIÓN INDUSTRIAL</span> EMPIEZA ACÁ!</h2>
          <p className="mx-auto mt-3 max-w-md text-[13px] leading-relaxed text-blue-100/80 md:text-sm" data-id="element-329" data-mp="1ntqj3vci3kh3">Accedé hoy a PLC MASTER, con todos sus bonos incluidos y acceso inmediato de por vida.</p>

          <img src="https://cdn.magicpatterns.com/uploads/hmqNJ8pT1CBLWsJ2sPunM5/Pack_PLC_Master_y_Variadores_Industriales.webp" alt="Pack PLC MASTER y Variadores de Frecuencia" className="mx-auto mt-4 block h-auto w-full max-w-[330px] object-contain" data-id="element-330" data-mp="3dacaxvck1ezf" />

          <div className="mt-3 border-t border-[#D6A44B]/25 pt-3 text-left" data-id="element-331" data-mp="3n306kmrstcwk">
            <p className="text-center text-xs font-black tracking-wide text-[#E8C272]" data-id="element-332" data-mp="3i8m8udpnytoq">PLC MASTER + VARIADORES DE FRECUENCIA</p>
            <ul className="mx-auto mt-3 max-w-sm space-y-1.5 text-xs leading-snug text-blue-100/85" data-id="element-333" data-mp="3w3g99u0pwg0d">
              {['Manual PLC MASTER desde cero', 'Manual Variadores de Frecuencia', 'Programación Ladder, LOGO! V8 y S7-1200', 'Bonos reales de PLC MASTER incluidos'].map(item => <li key={item} className="flex items-start gap-2" data-id="element-334" data-mp="4vtxtf7rb98y"><Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[#E8C272]" aria-hidden="true" data-id="element-335" data-mp="2bli9g64q3bj0" />{item}</li>)}
            </ul>
          </div>

          <div className="mt-4 rounded-lg border border-blue-200/60 bg-blue-50/10 px-3 py-2.5 text-left" data-id="element-336" data-mp="26o8c84b3dvkm">
            <p className="flex items-center gap-1.5 text-xs font-black tracking-wide text-blue-100" data-id="element-337" data-mp="1ksyk10riwkfm"><Download className="h-4 w-4 flex-shrink-0 text-[#E8C272]" aria-hidden="true" data-id="element-338" data-mp="1pr9undpn6yyw" /> FORMATO 100% DIGITAL</p>
            <p className="mt-1 text-[11px] leading-relaxed text-blue-100/80" data-id="element-339" data-mp="1uk3063sxqbc1">Manuales en PDF + clases en video + recursos de simulación PLC. Acceso inmediato, sin envío físico.</p>
          </div>
          <p className="mt-4 text-4xl font-black tracking-tight text-[#39D98A]" data-id="element-340" data-mp="11wgmu3twnsbz">AR$ 19.990</p>
          <div className="mt-4 rounded-xl border border-[#D6A44B]/80 bg-[#10254b] px-4 py-3 text-left shadow-[0_0_14px_rgba(214,164,75,0.12)]" data-id="element-341" data-mp="16u8o3ogfwqsu">
            <p className="flex items-center gap-2 text-sm font-black text-[#E8C272]" data-id="element-342" data-mp="1grmuev8nfz7f"><Gift className="h-4 w-4 flex-shrink-0" aria-hidden="true" data-id="element-343" data-mp="1gptmzdq7payd" /> ¡DESBLOQUEÁ UN 70% OFF EXTRA!</p>
            <p className="mt-1.5 text-xs leading-relaxed text-blue-100/85" data-id="element-344" data-mp="2naba58dbhpfj">Al avanzar con tu compra de PLC MASTER, en el siguiente paso vas a tener la oportunidad de sumar el Combo Industrial Completo con un 70% de descuento exclusivo.</p>
            <p className="mt-2 text-[11px] leading-snug text-[#E8C272]" data-id="element-345" data-mp="bkd7251ud9ub">La oportunidad se presenta dentro del flujo de compra.</p>
          </div>

          <button type="button" onClick={handlePurchaseClick} className="mt-5 flex min-h-[62px] w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 text-center text-sm font-black text-white shadow-[0_6px_0_#167a45,0_14px_24px_rgba(22,163,74,0.24)] transition-[transform,box-shadow,background-color] duration-180 ease-out hover:bg-emerald-500 active:translate-y-[2px] active:shadow-[0_3px_0_#167a45,0_8px_14px_rgba(22,163,74,0.2)] md:text-base" data-id="element-346" data-mp="6s25kxrgdfzn"><Zap className="h-5 w-5 flex-shrink-0" aria-hidden="true" data-id="element-347" data-mp="3f25808xyh44n" /> SÍ, QUIERO PLC MASTER <ArrowRight className="h-5 w-5 flex-shrink-0" aria-hidden="true" data-id="element-348" data-mp="1i8tl01hogpih" /></button>

          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[10px] font-bold tracking-wide text-blue-100/80" data-id="element-349" data-mp="1d9z3r3zcz90h">
            <span className="inline-flex items-center gap-1" data-id="element-350" data-mp="252qhndx86qae"><Lock className="h-3.5 w-3.5 text-[#E8C272]" aria-hidden="true" data-id="element-351" data-mp="204yqim4en879" /> PAGO SEGURO</span>
            <span className="inline-flex items-center gap-1" data-id="element-352" data-mp="207b82y693gjs"><Zap className="h-3.5 w-3.5 text-[#E8C272]" aria-hidden="true" data-id="element-353" data-mp="1vd70ccp5v4hn" /> ACCESO INMEDIATO</span>
            <span className="inline-flex items-center gap-1" data-id="element-354" data-mp="1qedun6g48hs1"><Download className="h-3.5 w-3.5 text-[#E8C272]" aria-hidden="true" data-id="element-355" data-mp="1qdb1ervbm38y" /> ACCESO DE POR VIDA</span>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 bg-white" style={{
      paddingTop: "0px"
    }} data-id="element-356" data-mp="1hd5wqphihiyh">
        <img src="https://cdn.magicpatterns.com/uploads/uiAkrJRfHpcu4UFuy6ko6E/capture_261005_175233.png" alt="Referencias técnicas argentinas AEA 90364 e IRAM" className="mx-auto block w-full max-w-[330px] object-contain" data-id="element-357" data-mp="1cfwb7kx8trfx" /><div className="container mx-auto max-w-3xl" data-id="element-358" data-mp="36sm3jy7yqsl7">
          <div className="text-center mb-12" data-id="element-359" data-mp="31u2gtvljpcrs">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" data-id="element-360" data-mp="31s7vwha9xknp">
              Resolvé acá tus dudas
            </h2>
            <p className="text-lg text-gray-600" data-id="element-361" data-mp="3ecof0f4hgsm2">
              Hacé clic para ver la respuesta que necesitás
            </p>
          </div>

          <div className="space-y-3" data-id="element-362" data-mp="3j4xxxb76kseu">
            <FAQItem question="¿Qué tipo de contenido incluye PLC MASTER?" answer="Incluye un manual completo de lógica y programación industrial, con explicación clara de Ladder, Bloques, y ejemplos aplicados al trabajo profesional." data-id="element-363" data-mp="3j5zcvqrbhz1u" />
            <FAQItem question="¿Necesito ser ingeniero para entender este material?" answer="No. El material está diseñado para técnicos y electricistas, explicando los conceptos de forma clara y progresiva, enfocándose en la lógica de campo." data-id="element-364" data-mp="2vdagrkzq0vpr" />
            <FAQItem question="¿Voy a aprender a interpretar lógica Ladder y Bloques?" answer="Sí. El foco principal es ayudarte a interpretar, entender y estructurar programas en lenguajes Ladder y Bloques de función." data-id="element-365" data-mp="35a5bdssvibyr" />
            <FAQItem question="¿Incluye ejemplos prácticos de fallas reales?" answer="Sí. El contenido incluye una biblioteca de lógica para fallas comunes y situaciones reales de planta." data-id="element-366" data-mp="3r39czb6e5bj4" />
            <FAQItem question="¿En qué formato recibo el material y los simuladores?" answer="Todo el contenido se entrega en formato digital con acceso inmediato a la plataforma donde podrás descargar los manuales y acceder a los simuladores." data-id="element-367" data-mp="3m6ixaoyi0hy8" />
            <FAQItem question="¿Tengo acceso para siempre incluyendo actualizaciones?" answer="Sí. El acceso es permanente y recibirás todas las actualizaciones futuras sin costo adicional." data-id="element-368" data-mp="38dtbakiyzd14" />
            <FAQItem question="¿Necesito conocimientos avanzados de informática?" answer="No. Si sabes usar una PC básica, puedes seguir el programa. Nos enfocamos en la lógica, no en la informática compleja." data-id="element-369" data-mp="3i2thanvw38ot" />
          </div>
        </div>
      </section>

      <UpsellSection data-id="element-370" data-mp="2pjxkhjwiaue4" />

      <section className="border-t border-slate-200 bg-slate-50 px-4 py-8" aria-label="Referencias técnicas" data-id="element-371" data-mp="2knpaekx61rdt">
        <div className="mx-auto max-w-sm text-center" data-id="element-372" data-mp="104v7d62gn2vg">
          
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-gray-900 text-gray-400 py-10 px-4 text-center text-sm border-t border-gray-800" data-id="element-373" data-mp="14wn1troqlsqv">
        <div className="container mx-auto" data-id="element-374" data-mp="q8jtibn824yw">
          <div className="w-12 h-12 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-375" data-mp="v61wllek5pyz">
            <img src="https://cdn.magicpatterns.com/uploads/v3vJ7LASUzqrw9kw6Gkeea/Diseno_sin_titulo_(5).png" alt="Logo" className="w-8 h-8 object-contain opacity-50 grayscale" data-id="element-376" data-mp="1ivw7vyj8b6h4" />
          </div>
          <p className="mb-2" data-id="element-377" data-mp="19v819ow608e9">
            &copy; {new Date().getFullYear()} PLC MASTER. Todos los derechos
            reservados.
          </p>
          <p className="text-gray-500" data-id="element-378" data-mp="1duyo5hikm4di">
            Lógica y programación industrial para profesionales.
          </p>
        </div>
      </footer>
    </div>;
}